import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Key, Plus, Copy, Trash2, AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { jarvisAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRSuperAdmin } from '@/lib/utils';
import { Navigate } from 'react-router-dom';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function JarvisKeysPage() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('Jarvis Production Key');
  const [creating, setCreating] = useState(false);
  const [revealKey, setRevealKey] = useState(null); // shown ONCE after creation
  const [revokeTarget, setRevokeTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await jarvisAPI.listKeys();
      setKeys(r.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isKRSuperAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await jarvisAPI.createKey(name.trim() || 'JARVIS Key');
      setRevealKey(r.data);
      setCreateOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await jarvisAPI.revokeKey(revokeTarget.key_id);
      toast.success('Key revoked');
      setRevokeTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke key');
    }
  };

  const copy = (text, label = 'Copied') => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  const activeCount = keys.filter((k) => k.active).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="jarvis-keys-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Key className="w-6 h-6 text-amber-600" />
          <h1 className="text-2xl font-bold">JARVIS API Keys</h1>
        </div>
        <p className="text-sm text-slate-600">
          Manage machine-to-machine API keys used by your personal AI assistant.
          Keys are stored as one-way hashes — once generated, the plaintext is
          shown <strong>only once</strong>. Lost keys must be rotated.
        </p>
      </div>

      {/* Quick reference card */}
      <Card className="p-4 mb-6 bg-slate-50 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-slate-500 text-xs uppercase mb-1">Base URL</div>
            <code className="text-xs break-all">{BASE_URL}/api/jarvis</code>
          </div>
          <div>
            <div className="text-slate-500 text-xs uppercase mb-1">Auth header</div>
            <code className="text-xs">X-JARVIS-API-KEY</code>
          </div>
          <div>
            <div className="text-slate-500 text-xs uppercase mb-1">Active keys</div>
            <div className="font-semibold">{activeCount}</div>
          </div>
        </div>
      </Card>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">All keys</h2>
        <Button
          onClick={() => setCreateOpen(true)}
          data-testid="jarvis-create-key-btn"
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Plus className="w-4 h-4 mr-1" /> Generate New Key
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : keys.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No keys yet. Click <strong>Generate New Key</strong> to create one.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="jarvis-keys-table">
            <thead className="bg-slate-50 border-b text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Prefix</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">Last used</th>
                <th className="text-right px-4 py-2">Uses</th>
                <th className="text-right px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {keys.map((k) => (
                <tr key={k.key_id} data-testid={`jarvis-key-row-${k.key_id}`}>
                  <td className="px-4 py-3 font-medium">{k.name}</td>
                  <td className="px-4 py-3"><code className="text-xs">{k.key_prefix}</code></td>
                  <td className="px-4 py-3">
                    {k.active ? (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                        <ShieldCheck className="w-3 h-3 mr-1" /> Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">Revoked</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(k.created_at)}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(k.last_used_at)}</td>
                  <td className="px-4 py-3 text-right">{k.use_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    {k.active && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRevokeTarget(k)}
                        data-testid={`jarvis-revoke-${k.key_id}`}
                        title="Revoke key"
                      >
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="jarvis-create-dialog">
          <DialogHeader>
            <DialogTitle>Generate New JARVIS API Key</DialogTitle>
            <DialogDescription>
              Creating a new key will <strong>revoke all currently active keys</strong>.
              Make sure no other client is using the old key before continuing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Key name (for your reference)</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Jarvis Railway Production"
              data-testid="jarvis-key-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !name.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="jarvis-confirm-create"
            >
              {creating ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating...</> : 'Generate Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal once dialog */}
      <Dialog open={!!revealKey} onOpenChange={(o) => !o && setRevealKey(null)}>
        <DialogContent className="max-w-2xl" data-testid="jarvis-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" /> Save this key now
            </DialogTitle>
            <DialogDescription>
              This is the only time you will ever see the full key. Copy it into
              your Railway environment variables (or password manager) before
              closing this dialog. If you lose it, you can only rotate to a new one.
            </DialogDescription>
          </DialogHeader>

          {revealKey && (
            <div className="space-y-3 text-sm">
              <Field label="API key (plaintext)" value={revealKey.api_key} mono onCopy={copy} testId="jarvis-reveal-key" />
              <Field label="KEY_ID" value={revealKey.key_id} mono onCopy={copy} testId="jarvis-reveal-key-id" />
              <Field label="Base URL" value={`${BASE_URL}/api/jarvis`} mono onCopy={copy} testId="jarvis-reveal-base" />
              <Field label="Auth header name" value={revealKey.header_name || 'X-JARVIS-API-KEY'} mono onCopy={copy} />

              <div className="border-t pt-3 mt-3">
                <div className="text-xs uppercase text-slate-500 mb-1">Railway environment variables (copy-paste block)</div>
                <RailwayBlock
                  apiKey={revealKey.api_key}
                  keyId={revealKey.key_id}
                  baseUrl={`${BASE_URL}/api/jarvis`}
                  onCopy={copy}
                />
              </div>

              {revealKey.previous_keys_revoked > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {revealKey.previous_keys_revoked} previously-active key{revealKey.previous_keys_revoked > 1 ? 's were' : ' was'} revoked.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              onClick={() => setRevealKey(null)}
              data-testid="jarvis-reveal-close"
            >
              I've saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent data-testid="jarvis-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this key?</AlertDialogTitle>
            <AlertDialogDescription>
              Any client using <code>{revokeTarget?.key_prefix}</code> will start
              getting <strong>401 Invalid or revoked API key</strong> immediately.
              You cannot undo this — you would need to mint a new key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="jarvis-confirm-revoke"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Field = ({ label, value, mono, onCopy, testId }) => (
  <div>
    <div className="text-xs uppercase text-slate-500 mb-1">{label}</div>
    <div className="flex items-center gap-2">
      <code
        className={`flex-1 bg-slate-100 rounded px-2 py-1.5 text-xs break-all ${mono ? 'font-mono' : ''}`}
        data-testid={testId}
      >
        {value}
      </code>
      <Button size="sm" variant="outline" onClick={() => onCopy(value, `${label} copied`)}>
        <Copy className="w-3 h-3" />
      </Button>
    </div>
  </div>
);

const RailwayBlock = ({ apiKey, keyId, baseUrl, onCopy }) => {
  const block = [
    `EMERGENT_BASE_URL=${baseUrl}`,
    `EMERGENT_API_KEY=${apiKey}`,
    `EMERGENT_KEY_ID=${keyId}`,
    `EMERGENT_HEADER=X-JARVIS-API-KEY`,
  ].join('\n');
  return (
    <div className="flex items-start gap-2">
      <pre
        className="flex-1 bg-slate-900 text-slate-100 rounded px-3 py-2 text-xs overflow-auto"
        data-testid="jarvis-railway-block"
      >
        {block}
      </pre>
      <Button size="sm" variant="outline" onClick={() => onCopy(block, 'Railway block copied')}>
        <Copy className="w-3 h-3" />
      </Button>
    </div>
  );
};
